import angular from 'angular'
import api from 'components/api'
import localStorageService from 'common/local-storage.service'

/**
 * OfflineScheduleService
 *
 * Two-layer offline cache:
 *   1. Pre-fetch: On startup/reconnect, fetches the full active playlist from backend
 *      (scene/getOfflinePlaylist) — gives instant full cache with correct playData shape.
 *   2. Playback cache: As items play, cachePlayedItem() keeps the cache fresh
 *      with the exact data the player consumed.
 *
 * Active-collection tracking (via backend API + WebSocket subscription) is kept so we
 * know when to invalidate the cache (playlist switch or "nothing active").
 */
class OfflineScheduleService {
  constructor ($log, ApiService, LocalStorageService) {
    'ngInject'

    this.$log = $log
    this.ApiService = ApiService
    this.LocalStorageService = LocalStorageService

    this._sceneId = null
    this._activeCollectionId = null
    this._offlineSchedule = [] // cached items (flat playData shape)
    this._currentIndex = 0
    this._offlineIdCounter = 0
    this._lastPlayedSourceId = null // tracks where to resume on offline switch

    this.STORAGE_KEY = 'offline_schedule_v2'
  }

  // ── Public API ──────────────────────────────────────────────────────

  startSync (sceneId) {
    this._sceneId = sceneId
    this.$log.info('[OfflineSchedule] Starting sync for scene:', sceneId)

    this._loadFromStorage()
    this._fetchFullPlaylist()

    // Re-fetch when playlist items are added/removed/reordered
    this.ApiService.subscribeToModel('scenecollection_scenecollectionitem', {}, this._onItemChange.bind(this))

    // Re-fetch when a SceneCollectionItem itself is deleted
    // (.unrelate() on through-model does NOT trigger hooks, but the item $query().delete() does)
    this.ApiService.subscribeToModel('scenecollectionitem', {}, this._onSceneCollectionItemChange.bind(this))

    // Re-fetch when schedule intervals change (playlist switch via time-based scheduling)
    this.ApiService.subscribeToModel('interval', {}, this._onIntervalChange.bind(this))
  }

  /**
   * Called on reconnect — re-fetches the full playlist from backend.
   * If collection changed or disappeared while offline the cache is replaced/cleared.
   */
  onReconnect () {
    this.$log.info('[OfflineSchedule] Reconnected — re-fetching playlist')
    this._fetchFullPlaylist()
  }

  /**
   * Called by the Player after itemData has been spread onto the playlistItem
   * and the player was successfully created. Stores a minimal clone that
   * PlayerFactory can consume directly.
   *
   * @param {object} playedItem — the flat item (after itemData spread) with id, type, fileType, url, etc.
   * @param {boolean} isOfflineItem — true if this item originated from the offline cache
   */
  cachePlayedItem (playedItem, isOfflineItem) {
    if (isOfflineItem) return // never re-cache items we produced ourselves
    if (!playedItem || !playedItem.type) return
    // Social items are served from a rotating pool — caching them causes unbounded growth.
    // The _fetchFullPlaylist snapshot already provides a stable set for offline use.
    if (playedItem.type === 'social') return

    const clone = this._cloneForCache(playedItem)
    if (!clone) return

    // Deduplicate: replace existing entry with same original sourceId, or append
    const existingIdx = this._offlineSchedule.findIndex(i => i._sourceId === clone._sourceId)
    if (existingIdx !== -1) {
      this._offlineSchedule[existingIdx] = clone
    } else {
      this._offlineSchedule.push(clone)
    }

    this._lastPlayedSourceId = clone._sourceId
    this.$log.debug('[OfflineSchedule] Cached item', clone._sourceId, '— total:', this._offlineSchedule.length)
    this._saveToStorage()
  }

  /**
   * Called by the Player when an item starts playing.
   * Tracks which collection is active based on the sceneCollectionId present on played items.
   */
  notifyActiveCollection (sceneCollectionId) {
    if (!sceneCollectionId) return
    if (sceneCollectionId === this._activeCollectionId) return

    this.$log.info('[OfflineSchedule] Active collection changed from playback:', this._activeCollectionId, '→', sceneCollectionId)
    this._activeCollectionId = sceneCollectionId
    // New collection — clear old cached items so they rebuild from fresh playback
    this._offlineSchedule = []
    this._currentIndex = 0
    this._saveToStorage()
  }

  /**
   * Returns the next cached item formatted as a PlaylistItem-like object.
   * Returns null if cache is empty.
   */
  getNextItem () {
    if (!this._offlineSchedule || this._offlineSchedule.length === 0) {
      return null
    }

    // On first offline call, resume from where the server left off
    if (this._lastPlayedSourceId) {
      const lastIdx = this._offlineSchedule.findIndex(i => i._sourceId === this._lastPlayedSourceId)
      if (lastIdx !== -1) {
        this._currentIndex = (lastIdx + 1) % this._offlineSchedule.length
      }
      this._lastPlayedSourceId = null // only seek once per offline transition
    }

    if (this._currentIndex >= this._offlineSchedule.length) {
      this._currentIndex = 0
    }

    const cached = this._offlineSchedule[this._currentIndex]
    this._currentIndex++

    if (!cached) {
      this.$log.error('[OfflineSchedule] getNextItem: item at index', this._currentIndex - 1, 'is undefined')
      return null
    }

    // Build a PlaylistItem-like object with a unique offline ID
    this._offlineIdCounter++
    const offlineId = `offline-${this._offlineIdCounter}`

    return {
      ...cached,
      id: offlineId,
      _isOfflineItem: true
    }
  }

  // ── Private ─────────────────────────────────────────────────────────

  /**
   * Derives a stable media-level key for deduplication.
   * Uses url (unique per media file / social post) or headline for service announcements.
   */
  _deriveSourceId (item) {
    if (item.url) return item.url
    if (item.headline) return `sa_${item.headline}`
    return null
  }

  /**
   * Clones only the fields PlayerFactory + player components actually need,
   * keeping localStorage usage minimal.
   */
  _cloneForCache (item) {
    const base = {
      _sourceId: this._deriveSourceId(item) || item.id,
      type: item.type,
      duration: item.duration,
      sceneCollectionId: item.sceneCollectionId
    }

    switch (item.type) {
      case 'file':
        if (!item.fileType || !item.url) return null
        base.fileType = item.fileType
        base.url = item.url
        base.width = item.width
        base.height = item.height
        base.name = item.name
        break

      case 'social':
        base.fileType = item.fileType
        base.url = item.url
        base.username = item.username
        base.caption = item.caption
        base.name = item.name
        break

      case 'serviceAnnouncement':
        base.headline = item.headline
        base.body = item.body
        base.name = item.name
        break

      default:
        return null
    }

    return base
  }

  _fetchFullPlaylist () {
    this.ApiService.call('scene/getOfflinePlaylist', { sceneId: this._sceneId })
      .then(response => {
        if (!response || !response.sceneCollectionId) {
          // No active playlist — clear everything to prevent phantom playback
          this.$log.info('[OfflineSchedule] No active collection — clearing cache')
          this._activeCollectionId = null
          this._offlineSchedule = []
          this._currentIndex = 0
          this._saveToStorage()
          return
        }

        this.$log.info(`[OfflineSchedule] Fetched ${response.items.length} items for collection ${response.sceneCollectionId}`)
        this._activeCollectionId = response.sceneCollectionId
        this._offlineSchedule = response.items.map(item => ({
          ...item,
          _sourceId: this._deriveSourceId(item) || item._sourceId
        }))
        this._currentIndex = 0
        this._saveToStorage()
      })
      .catch(err => {
        // Offline at startup — rely on stale localStorage cache
        this.$log.debug('[OfflineSchedule] _fetchFullPlaylist failed (offline?)', err)
      })
  }

  _onItemChange (err, change) {
    if (err) return
    // If the change relates to the currently active collection, re-fetch the full playlist
    if (this._activeCollectionId && change.sceneCollectionId === this._activeCollectionId) {
      this.$log.info('[OfflineSchedule] Active playlist modified — re-fetching')
      this._fetchFullPlaylist()
    }
  }

  _onSceneCollectionItemChange (err, change) {
    if (err) return
    // When a SceneCollectionItem is removed, re-fetch to stay in sync.
    // We can't filter by collection here (the delete broadcast lacks sceneCollectionId),
    // so we re-fetch on any removal. This is infrequent and cheap.
    if (change.removed) {
      this.$log.info('[OfflineSchedule] SceneCollectionItem removed — re-fetching')
      this._fetchFullPlaylist()
    }
  }

  _onIntervalChange (err, change) {
    if (err) return
    // Any interval change may switch the active playlist — re-fetch to pick up the new one
    this.$log.info('[OfflineSchedule] Interval changed — re-fetching playlist')
    this._fetchFullPlaylist()
  }

  _saveToStorage () {
    const data = {
      sceneId: this._sceneId,
      activeCollectionId: this._activeCollectionId,
      schedule: this._offlineSchedule,
      timestamp: Date.now()
    }
    this.LocalStorageService.set(this.STORAGE_KEY, data)
  }

  _loadFromStorage () {
    const data = this.LocalStorageService.get(this.STORAGE_KEY)
    if (data && data.sceneId === this._sceneId) {
      const schedule = Array.isArray(data.schedule) ? data.schedule : []
      this._offlineSchedule = schedule
      this._activeCollectionId = data.activeCollectionId
      this.$log.info('[OfflineSchedule] Loaded from storage:', this._offlineSchedule.length, 'items')
    }
  }
}

export default angular.module('offline-schedule.service', [
  api,
  localStorageService
])
  .service('OfflineScheduleService', OfflineScheduleService)
  .name
